import React from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { SlRichText } from '../SlRichText/SlRichText';
import { SlCodeSnippet } from '../SlCodeSnippet/SlCodeSnippet';
import { SlImage } from '../SlImage/SlImage';
import { SlNote } from '../SlNote/SlNote';
import { SlSingleChoiceAnswer } from '../SlSingleChoiceAnswer/SlSingleChoiceAnswer';
import { SlMultiChoiceAnswer } from '../SlMultiChoiceAnswer/SlMultiChoiceAnswer';
import { SlRearrangeAnswer } from '../SlRearrangeAnswer/SlRearrangeAnswer';
import { SlTypeInAnswer } from '../SlTypeInAnswer/SlTypeInAnswer';
import { SlDragAndDropAnswerV2 } from '../SlDragAndDropAnswerV2/SlDragAndDropAnswerV2';
import { SlImageSingleChoiceAnswer } from '../SlImageSingleChoiceAnswer/SlImageSingleChoiceAnswer';
import { ISlAnswerProps } from '../../globalInterfaces/learnEngineInterfaces';

import s from './SlUIComponentsRenderer.scss';
import { IMaterialBody, UIComponentType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlRiveAnimation } from '../SlRiveAnimation/SlRiveAnimation';

export interface ISlUIComponentsRendererProps {
    components: IMaterialBody[];
    answerProps?: ISlAnswerProps;
    onClick?: (a: UIComponentType) => void;
    disabled?: boolean;
}

export function SlUIComponentsRenderer({
	components,
	answerProps,
	onClick,
	disabled,
}: ISlUIComponentsRendererProps): JSX.Element {
	useStyles(s);
	const renderComponent = (component: IMaterialBody) => {
		switch (component.componentTypeId) {
		case UIComponentType.RichText:
			return (
				<SlRichText
					key={component.orderNumber}
					content={component.content.data}
				/>
			);
		case UIComponentType.CodeSnippet:
			return (
				<SlCodeSnippet
					key={component.orderNumber}
					content={component.content.data}
					language={component.content.language}
					onClick={onClick}
				/>
			);
		case UIComponentType.Image:
		case UIComponentType.Gif:
			return (
				<SlImage
					key={component.orderNumber}
					path={component.content.data}
					ratio={component.content.ratio}
					onClick={onClick}
				/>
			);
		case UIComponentType.Note:
			return (
				<SlNote
					key={component.orderNumber}
					content={component.content.data}
					level={component.content.level}
				/>
			);
		case UIComponentType.SingleChoice:
			return (
				<SlSingleChoiceAnswer
					key={component.orderNumber}
					{...answerProps}
				/>
			);
		case UIComponentType.MultiChoice:
			return (
				<SlMultiChoiceAnswer
					key={component.orderNumber}
					{...answerProps}
				/>
			);
		case UIComponentType.Rearrange:
			return (
				<SlRearrangeAnswer
					key={component.orderNumber}
					{...answerProps}
				/>
			);
		case UIComponentType.DragAndDrop:
			return (
				<SlDragAndDropAnswerV2
					uiType={component?.config?.uiType}
					instructions={component.content?.instructions}
					key={component.orderNumber}
					{...answerProps}
					questionParts={component.content.parts}
				/>
			);
		case UIComponentType.TypeIn:
			return (
				<SlTypeInAnswer
					uiType={component?.config?.uiType}
					key={component.orderNumber}
					questionParts={[component.content.part]}
					{...answerProps}
					disabled={disabled}
				/>
			);
		case UIComponentType.MultiTypeIn:
			return (
				<SlTypeInAnswer
					uiType={component?.config?.uiType}
					key={component.orderNumber}
					{...answerProps}
					questionParts={component.content.parts}
					disabled={disabled}
				/>
			);
		case UIComponentType.RiveAnimation:
			return (
				<SlRiveAnimation
					path={component.config?.rive?.webAssetUrl}
					ratio={component.config?.rive?.webRatio}
					key={component.orderNumber}
				/>
			);
		case UIComponentType.ImageSingleChoice:
			return (
				<SlImageSingleChoiceAnswer
					key={component.orderNumber}
					{...answerProps}
				/>
			);
		default:
			return null;
		}
	};

	return (
		<>
			{components.map((component) => (
				<div
					className="sl-ui-component"
					key={component.orderNumber}
				>
					{renderComponent(component)}
				</div>
			))}
		</>
	);
}
